import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { useWebSocket } from "../../../hooks/useWebSocket";
import { fetchAnnualPerformance } from "../../../utils/restAPI";
import { transformAnnualPerformanceData } from "../../../utils/transformers";
import {
  selectStrategiesForPeriod,
  selectCurrentPeriod,
  setStrategiesForPeriod,
  TIME_PERIODS,
} from "../../../Redux/Reducers/strategyStoreSlice";
import { selectStrategies } from "../../../Redux/Reducers/strategiesSlice";
import AnnualPerformanceTable from "../components/AnnualPerfomanceTable";

/**
 * TODO: Remove this hook when single strategy endpoint is available.
 *
 */
const useStrategyDetailData = (strategyId, period) => {
  const dispatch = useDispatch();
  const { send, subscribeToMessage } = useWebSocket();
  const [loading, setLoading] = useState(false);
  const [strategyMetadata, setStrategyMetadata] = useState(null);
  const pendingPeriodRef = useRef(null);

  // Get strategies from both sources
  const strategiesFromStore =
    useSelector(selectStrategiesForPeriod(period)) || [];
  const seedStrategies = useSelector(selectStrategies) || [];

  const strategies = strategiesFromStore;
  const strategy = strategies.find(
    (strategy) => strategy.idStrategy === strategyId,
  );

  // Get metadata from seed data
  const seedStrategy = seedStrategies.find((s) => s.strategyId === strategyId);

  const fetchStrategies = useCallback(
    (period) => {
      pendingPeriodRef.current = period;
      setLoading(true);
      send(
        JSON.stringify({
          action: "theoretical_performance_marketdata",
          data: {
            timeframe: { Daily: "D", Weekly: "W", Monthly: "M", Annually: "Y" }[
              period
            ],
          },
        }),
      );
    },
    [send],
  );

  useEffect(() => {
    const handleResponse = (data) => {
      if (
        data.action === "theoretical_performance_marketdata" &&
        data.data &&
        pendingPeriodRef.current
      ) {
        const transformed = data.data.map((strategy) => ({
          idStrategy: strategy.strategyId,
          title: strategy.strategyName,
          description: `Period Return: ${(strategy.periodReturn * 100).toFixed(2)}%`,
          chartData: strategy.returns.map((returnItem) => ({
            date: returnItem.date,
            value: returnItem.return,
          })),
          periodReturn: strategy.periodReturn,
          sharpeRatio: strategy.sharpeRatio || 0,
        }));
        dispatch(
          setStrategiesForPeriod({
            period: pendingPeriodRef.current,
            strategies: transformed,
          }),
        );
        pendingPeriodRef.current = null;
        setLoading(false);
      }
    };
    subscribeToMessage("theoretical_performance_marketdata", handleResponse);
  }, [subscribeToMessage, dispatch]);

  useEffect(() => {
    if (!strategies || strategies.length === 0) {
      fetchStrategies(period);
    }
  }, [period, strategies, fetchStrategies]);

  // Extract metadata from seed data
  useEffect(() => {
    if (seedStrategy && seedStrategy.metadata) {
      setStrategyMetadata(seedStrategy.metadata);
    }
  }, [seedStrategy]);

  return { strategy, loading, strategyMetadata };
};

const StrategyDetail = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [annualPerformanceData, setAnnualPerformanceData] = useState(null);

  const globalPeriod = useSelector(selectCurrentPeriod);
  const [localPeriod, setLocalPeriod] = useState(globalPeriod);

  // TODO: Replace useStrategyDetailData with direct API call when available
  const { strategy, loading: strategyLoading } = useStrategyDetailData(
    id,
    localPeriod,
  );

  // Fetch annual performance data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const response = await fetchAnnualPerformance(id);
        if (response.success && response.data) {
          const transformedData = transformAnnualPerformanceData(response.data);
          setAnnualPerformanceData(transformedData);
        }
      } catch (error) {
        console.error("Failed to fetch annual performance:", error);
      }
    };

    fetchData();
  }, [id]);

  const handlePeriodChange = (event) => {
    setLocalPeriod(event.target.value);
  };

  if (strategyLoading || !strategy) {
    return (
      <Box py={3}>
        <Container maxWidth="xl">
          <Card sx={{ p: 3, mt: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="text"
              onClick={() => navigate("/store")}
              sx={{
                ":hover": {
                  transform: "none",
                  boxShadow: "none",
                  backgroundColor: "none",
                },
              }}
            >
              Back to Store
            </Button>
            {strategyLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Typography variant="h5" gutterBottom>
                Strategy not found
              </Typography>
            )}
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title> {strategy?.title || "Strategy"} | Vantage Alpha</title>
      </Helmet>
      <Box>
        <Container maxWidth="xl">
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
              mt: 4,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              variant="text"
              onClick={() => navigate("/store")}
              sx={{
                ":hover": {
                  transform: "none",
                  boxShadow: "none",
                  backgroundColor: "none",
                },
              }}
            >
              Back
            </Button>

            <Typography variant="h4" gutterBottom>
              {strategy.title}
            </Typography>

            <Typography variant="body1" color="text.secondary" mb={3}>
              {strategy.description}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 3,
              }}
            >
              <MetricCard
                title="Period Return"
                value={`${(strategy.periodReturn * 100).toFixed(2)}%`}
                color={
                  strategy.periodReturn >= 0 ? "success.main" : "error.main"
                }
              />
              <MetricCard
                title="Sharpe Ratio"
                value={strategy.sharpeRatio.toFixed(4)}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Select
                value={localPeriod}
                onChange={handlePeriodChange}
                size="small"
                sx={{ minWidth: 120 }}
              >
                {TIME_PERIODS.map((period) => (
                  <MenuItem key={period} value={period}>
                    {period}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Chart
              data={strategy.chartData}
              annualData={annualPerformanceData}
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};

/**
 * Chart
 * @param {{date: string, value: number}[]} data
 */
const MetricCard = ({ title, value, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h4" color={color} fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
};

const Chart = ({ data = [], annualData = null }) => {
  const currentTheme = useTheme();
  const values = Array.isArray(data)
    ? data.map((dataPoint) => Number(dataPoint.value))
    : [];
  const hasData =
    values.length > 0 && values.every((value) => !Number.isNaN(value));
  const min = hasData ? Math.min(...values) : 0;
  const max = hasData ? Math.max(...values) : 1;
  const pad = Math.max((max - min) * 0.1, 0.02);
  const domain = [min - pad, max + pad];

  const globalPeriod = useSelector(selectCurrentPeriod);

  function buildFormattedData(data) {
    let lastRenderedYear = null;
    let stepIndex = 0;

    return data.map((point) => {
      const fullDate = point.date; // Format: YYYY-MM-DD
      const currentYear = fullDate.slice(0, 4);
      const monthDay = fullDate.slice(5); // MM-DD

      let xAxisDate = "";

      const isNewYear = currentYear !== lastRenderedYear;
      const isMonthly = globalPeriod === "Monthly";
      const isWeekly = globalPeriod === "Weekly";
      const shouldShowStep = stepIndex % 2 === 0;

      // If the year changes, show the full date and reset the step counter
      if (isNewYear) {
        xAxisDate = fullDate;
        lastRenderedYear = currentYear;
        stepIndex = 0;
      }
      // Otherwise, decide based on the selected period
      else {
        if ((isMonthly && shouldShowStep) || isWeekly) {
          xAxisDate = monthDay;
        }

        stepIndex++;
      }

      return {
        ...point,
        XaxisDate: xAxisDate,
      };
    });
  }

  const formattedData = buildFormattedData(data);

  return (
    <>
      <ResponsiveContainer width="100%" height={600}>
        <AreaChart
          data={formattedData}
          margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
        >
          <defs>
            <linearGradient id="miniColorValue" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={currentTheme.palette.primary.main}
                stopOpacity={0.7}
              />
              <stop
                offset="95%"
                stopColor={currentTheme.palette.primary.main}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={currentTheme.palette.divider}
            vertical={false}
          />
          <XAxis
            dataKey="XaxisDate"
            interval="preserveStartEnd"
            tickLine={false}
          />
          <YAxis domain={domain} hide />
          <Tooltip
            formatter={(value) =>
              typeof value === "number" ? value.toFixed(3) : value
            }
            labelFormatter={(label, payload) =>
              `Date: ${payload?.[0]?.payload?.date}`
            }
            contentStyle={{
              backgroundColor: currentTheme.palette.background.default,
              border: `1px solid ${currentTheme.palette.divider}`,
              borderRadius: 8,
              color: currentTheme.palette.text.primary,
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={currentTheme.palette.primary.main}
            fillOpacity={1}
            fill="url(#miniColorValue)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {annualData && <AnnualPerformanceTable data={annualData} />}
    </>
  );
};
export default StrategyDetail;
